// src/constants/farmPulseConstants.js

/**
 * FarmPulse configuration constants.
 * All magic numbers, thresholds, and config live here
 */

// Validation: how many independent contributors must agree within MARGIN before a price is "validated"
export const VALIDATION_THRESHOLD = 5;
export const VALIDATION_MARGIN_PCT = 2;

// Impact points awarded per contribution
export const IP_PER_PRICE_SUBMISSION = 10;
export const IP_PER_PLANTING_SUBMISSION = 0;

// Firestore collection names
export const COLLECTIONS = {
  PRODUCTS: "products",
  OFFERS: "offers",
  PRICE_SUBMISSIONS: "priceSubmissions",
  PLANTING_INTENTIONS: "plantingIntentions",
  USERS: "users",
  IMPACT_FEED: "impactFeed",
};

// Cache keys for localStorage
export const CACHE_KEYS = {
  PRODUCTS: "farmPulse.cache.products.v3",
  PRODUCTS_AT: "farmPulse.cache.at.v3",
  SUBMISSIONS: "farmPulse.cache.submissions.v1",
  PREDICTIONS: "farmPulse.cache.predictions.v1",
};

// Data window presets (days)
export const WINDOW_OPTIONS = [7, 30, 90, 180];

// Seasonal definitions for Rwanda (actual Rwandan seasons)
export const SEASONS = [
  { key: "ALL",      months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] },
  { key: "ITUMBA",   months: [2, 3, 4] },      // Mar–May  (Long Rainy)
  { key: "IMPESHYI", months: [5, 6, 7, 8] },    // Jun–Sep  (Long Dry)
  { key: "UMUHINDO", months: [9, 10] },          // Oct–Nov  (Short Rainy)
  { key: "URUGARYI", months: [11, 0, 1] },       // Dec–Feb  (Short Dry)
];

// Seasonal price bias by crop (percent shift). Used to adjust predictions.
export const SEASON_PRIOR = {
  Tomatoes:         { ITUMBA: +5,  IMPESHYI: -10, UMUHINDO: +3,  URUGARYI: -5  },
  Cabbage:          { ITUMBA: -8,  IMPESHYI: +6,  UMUHINDO: -5,  URUGARYI: +3  },
  Onions:           { ITUMBA: -6,  IMPESHYI: +8,  UMUHINDO: +2,  URUGARYI: +5  },
  Potatoes:         { ITUMBA: -3,  IMPESHYI: +5,  UMUHINDO: -2,  URUGARYI: +4  },
  Beans:            { ITUMBA: +4,  IMPESHYI: -6,  UMUHINDO: +3,  URUGARYI: -4  },
  Maize:            { ITUMBA: +6,  IMPESHYI: -8,  UMUHINDO: +4,  URUGARYI: -3  },
  Rice:             { ITUMBA: +3,  IMPESHYI: -4,  UMUHINDO: +2,  URUGARYI: -2  },
  Cassava:          { ITUMBA: -2,  IMPESHYI: +3,  UMUHINDO: -1,  URUGARYI: +2  },
  Bananas:          { ITUMBA: +2,  IMPESHYI: -3,  UMUHINDO: +1,  URUGARYI: -1  },
  "Sweet Potatoes": { ITUMBA: -4,  IMPESHYI: +5,  UMUHINDO: -2,  URUGARYI: +3  },
};

// Signal classification weights
export const SIGNAL_WEIGHTS = {
  trend30: 0.3,
  trend90: 0.25,
  volumeTrend30: 0.15,
  seasonAdj: 0.1,
  proj30Pct: 0.2,
};

// Signal thresholds
export const SIGNAL_THRESHOLDS = {
  STRONG_BUY: 20,
  BUY: 8,
  AVOID: -8,
  STRONG_AVOID: -20,
};

// Price status thresholds (percent)
export const PRICE_STATUS = {
  RISING_THRESHOLD: 3,
  FALLING_THRESHOLD: -3,
};

// Prediction horizons (days)
export const PREDICTION_HORIZONS = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
};

// Minimum data points required for prediction
export const MIN_POINTS_FOR_PREDICTION = 14;
export const MIN_POINTS_FOR_ARIMA = 30;

// Confidence thresholds
export const CONFIDENCE_LEVELS = {
  LOW: { min: 0, max: 0.4 },
  MEDIUM: { min: 0.4, max: 0.7 },
  HIGH: { min: 0.7, max: 1.0 },
};

// Units available for price submission
export const PRICE_UNITS = [
  "kg", "ton", "bag", "bundle", "piece", "liter", "crate", "sack", "basket", "heap",
];

// Highlight card limits
export const HIGHLIGHT_LIMIT = 6;
export const SUPPLY_ALERT_LIMIT = 4;

// Sort options
export const SORT_OPTIONS = ["gainers", "losers", "volume", "price"];

// How many days of data minimum for sparkline
export const SPARKLINE_MIN_POINTS = 2;

// PDF export settings
export const PDF_FILENAME = "FarmPulse_Market_Report.pdf";

// Submission cooldown (ms) — prevent multiple submissions within 1 minute
export const SUBMISSION_COOLDOWN_MS = 60_000;

// Rwanda-specific crop mapping (for category auto-detection)
export const RWANDA_TOP_CROPS = [
  "maize", "beans", "rice", "sorghum", "cassava", "potatoes",
  "sweet_potatoes", "bananas", "coffee", "tea", "tomatoes",
  "cabbages", "onions", "carrots", "avocados", "pineapples",
  "pyrethrum", "macadamia", "groundnuts", "soybeans",
  "wheat", "millet", "peas", "eggplants", "french_beans",
  "garlic", "passion_fruit", "papaya", "sugarcane",
];

// All 30 districts of Rwanda (for location selection)
export const RWANDA_DISTRICTS = [
  "Bugesera", "Burera", "Gakenke", "Gasabo", "Gatsibo",
  "Gicumbi", "Gisagara", "Huye", "Kamonyi", "Karongi",
  "Kayonza", "Kicukiro", "Kirehe", "Muhanga", "Musanze",
  "Ngoma", "Ngororero", "Nyabihu", "Nyagatare", "Nyamagabe",
  "Nyamasheke", "Nyanza", "Nyarugenge", "Nyaruguru", "Rubavu",
  "Ruhango", "Rulindo", "Rusizi", "Rutsiro", "Rwamagana",
];
